type Incident = {
  id: string;
  components: Array<{
    id: string;
    page_id: string;
    status:
      | "operational"
      | "under_maintenance"
      | "degraded_performance"
      | "partial_outage"
      | "major_outage";
  }>;
  created_at: string;
  impact: "none" | "maintenance" | "minor" | "major" | "critical";
  impact_override: "none" | "maintenance" | "minor" | "major" | "critical";
  monitoring_at: string;
  name: string;
  page_id: string;
  postmortem_body: string;
  postmortem_body_last_updated_at: string;
  postmortem_ignored: boolean;
  postmortem_notified_subscribers: boolean;
  postmortem_notified_twitter: boolean;
  postmortem_published_at: boolean;
  resolved_at: string;
  scheduled_auto_completed: boolean;
  scheduled_auto_in_progress: boolean;
  scheduled_for: string;
  auto_transition_deliver_notifications_at_end: boolean;
  auto_transition_deliver_notifications_at_start: boolean;
  auto_transition_to_maintenance_state: boolean;
  auto_transition_to_operational_state: boolean;
  scheduled_remind_prior: boolean;
  scheduled_reminded_at: string;
  scheduled_until: string;
  shortlink: string;
  status:
    | "investigating"
    | "identified"
    | "monitoring"
    | "resolved"
    | "scheduled"
    | "in_progress"
    | "verifying"
    | "completed";
  updated_at: string;
  reminder_intervals: string;
};

type ScheduledMaintenance = Incident;

function parseDateToHumanReadableFormat(date: Date): string {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hour = date.getHours().toString().padStart(2, "0");
  const minute = date.getMinutes().toString().padStart(2, "0");
  return day + "." + month + "." + year + " " + hour + ":" + minute;
}

const DIGIPOST_COMPONENT = "v0dc4b8glwwr";
const SIGNERING_COMPONENT = "9yxcrwwfqpcn";
const API_URL = `https://status.digipost.no/api/v2`;

async function get<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (res.ok) {
    return await res.json();
  }

  throw new Error("Could not get statuspage info");
}
function getScheduledMaintenances(status: "upcoming" | "active") {
  return get<{ scheduled_maintenances: Incident[] }>(
    `${API_URL}/scheduled-maintenances/${status}.json`,
  );
}
function getIncidents(status: "unresolved" | "resolved") {
  return get<{ incidents: Incident[] }>(`${API_URL}/incidents/${status}.json`);
}

export type MaintenanceInfo = {
  id: string;
  type: "scheduled_maintenance";
  status:
    | "investigating"
    | "identified"
    | "monitoring"
    | "resolved"
    | "scheduled"
    | "in_progress"
    | "verifying"
    | "completed";
  name: string;
  shortlink: string;
  impact: "none" | "maintenance" | "minor" | "major" | "critical";
  maintenanceDate: string;
  maintenanceEndDate: string;
};

export type IncidentInfo = {
  id: string;
  type: "incident";
  status:
    | "investigating"
    | "identified"
    | "monitoring"
    | "resolved"
    | "scheduled"
    | "in_progress"
    | "verifying"
    | "completed";
  name: string;
  shortlink: string;
  impact: "none" | "maintenance" | "minor" | "major" | "critical";
};

export type StatusPageInfo = MaintenanceInfo | IncidentInfo;

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function getUpcomingScheduledMaintenancesInfo(
  scheduledMaintenances: ScheduledMaintenance[],
): MaintenanceInfo[] {
  const maintenances = scheduledMaintenances
    .filter((maintenance) =>
      maintenance.components.some(
        (comp) =>
          comp.id === DIGIPOST_COMPONENT || comp.id === SIGNERING_COMPONENT,
      ),
    )
    .filter((maintenance) => {
      const maintenanceDate = new Date(maintenance.scheduled_for);
      const maintenanceEndDate = new Date(maintenance.scheduled_until);
      const oneDayBeforeMaintenance = new Date(
        maintenanceDate.getTime() - ONE_DAY_MS,
      );
      const now = new Date();
      return oneDayBeforeMaintenance < now && now < maintenanceEndDate;
    });

  return maintenances.map((maintenance) => {
    const maintenanceDate = parseDateToHumanReadableFormat(
      new Date(maintenance.scheduled_for),
    );
    const maintenanceEndDate = parseDateToHumanReadableFormat(
      new Date(maintenance.scheduled_until),
    );

    return {
      id: maintenance.id,
      type: "scheduled_maintenance",
      status: maintenance.status,
      name: maintenance.name,
      shortlink: maintenance.shortlink,
      impact: maintenance.impact,
      maintenanceDate,
      maintenanceEndDate,
    };
  });
}

function getActiveScheduledMaintenancesInfo(
  scheduledMaintenances: ScheduledMaintenance[],
): MaintenanceInfo[] {
  return scheduledMaintenances
    .filter((maintenance) =>
      maintenance.components.some(
        (comp) =>
          (comp.id === DIGIPOST_COMPONENT || comp.id === SIGNERING_COMPONENT) &&
          comp.status !== "operational",
      ),
    )
    .map((maintenance) => {
      const maintenanceDate = parseDateToHumanReadableFormat(
        new Date(maintenance.scheduled_for),
      );
      const maintenanceEndDate = parseDateToHumanReadableFormat(
        new Date(maintenance.scheduled_until),
      );

      return {
        id: maintenance.id,
        type: "scheduled_maintenance",
        status: maintenance.status,
        name: maintenance.name,
        shortlink: maintenance.shortlink,
        impact: maintenance.impact,
        maintenanceDate,
        maintenanceEndDate,
      };
    });
}

function getUnresolvedIncidentsInfo(
  unresolvedIncidents: Incident[],
): IncidentInfo[] {
  return unresolvedIncidents
    .filter((incident) => {
      return incident.components.some(
        (comp) =>
          comp.id === DIGIPOST_COMPONENT || comp.id === SIGNERING_COMPONENT,
      );
    })
    .map((incident) => ({
      id: incident.id,
      type: "incident",
      status: incident.status,
      name: incident.name,
      shortlink: incident.shortlink,
      impact: incident.impact,
    }));
}

export async function getStatusPageData(): Promise<StatusPageInfo[]> {
  try {
    const [upcoming, active, unresolved] = await Promise.all([
      getScheduledMaintenances("upcoming"),
      getScheduledMaintenances("active"),
      getIncidents("unresolved"),
    ]);

    return [
      ...getUnresolvedIncidentsInfo(unresolved.incidents),
      ...getActiveScheduledMaintenancesInfo(active.scheduled_maintenances),
      ...getUpcomingScheduledMaintenancesInfo(upcoming.scheduled_maintenances),
    ];
  } catch (error) {
    return [];
  }
}
